import { useState, useCallback } from 'react';
import { Button, ButtonSizeSM } from '@picsart/design-system/components/Button';
import {
  ControlSizesMD,
  InlineButton,
} from '@picsart/design-system/components/InlineButton';
import { IconImageOutline } from '@picsart/design-system/foundation/icons/IconImageOutline';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { useClickOutsideRef } from '@picsart/design-system/components/hooks';
import UserMenuButton from '@/components/UserMenuButton';
import SettingsPanel from '@/components/SettingsPanel';
import { SettingsPanelVariant } from '@/components/SettingsPanel/types';
import useUserProfile from '@/hooks/useUserProfile';
import useTouchpoint from '@/hooks/useTouchpoint';
import { useDeviceViewport } from '@/hooks/useViewport';
import useStyles from './styles';
import type { HeaderRightProps } from './types';

const HeaderRight = ({
  isAuthorized,
  showGenerationsButton,
  onLogin,
  onGenerationsClick,
  onCloseApp,
  onSettingsClick,
}: HeaderRightProps) => {
  const classes = useStyles();
  const { showUserMenu } = useTouchpoint();
  const {
    isAuthorized: userIsAuthorized,
    username,
    avatarSrc,
    tierId,
  } = useUserProfile();
  const { isMobile } = useDeviceViewport();
  const [showDesktopSettings, setShowDesktopSettings] = useState(false);
  const closeSettings = useCallback(() => setShowDesktopSettings(false), []);
  const settingsRef = useClickOutsideRef(closeSettings, [], false);

  const handleSettingsClick = () => {
    if (isMobile) {
      onSettingsClick?.();
    } else {
      setShowDesktopSettings((v) => !v);
    }
  };

  return (
    <div className={classes.right}>
      {/* Login button - shown when not authorized and user menu is hidden */}
      {!isAuthorized && !showUserMenu && (
        <Button
          size={ButtonSizeSM}
          onClick={onLogin}
          aria-label="Log in"
          title="Log in"
          dataTestId="header-right-login"
        >
          Log in
        </Button>
      )}
      {/* Generations button - CSS hides on desktop */}
      {showGenerationsButton && (
        <span className={classes.generationsButton}>
          <InlineButton
            size={ControlSizesMD}
            centerIcon={IconImageOutline}
            className={classes.iconButton}
            isWithoutBorder
            isWithoutBackground
            onClick={onGenerationsClick}
            aria-label="Your Creations"
            title="Your Creations"
            dataTestId="header-right-generations"
          />
        </span>
      )}
      {/* User menu or close button */}
      {showUserMenu ? (
        <div className={classes.settingsWrapper}>
          <UserMenuButton
            isAuthorized={userIsAuthorized}
            username={username}
            avatarSrc={avatarSrc}
            tierId={tierId}
            showUserMenu={showUserMenu}
            onCloseApp={onCloseApp}
            onSettingsClick={handleSettingsClick}
          />
          {showDesktopSettings && !isMobile && (
            <div ref={settingsRef}>
              <SettingsPanel
                variant={SettingsPanelVariant.Dropdown}
                isAuthorized={userIsAuthorized}
              />
            </div>
          )}
        </div>
      ) : (
        <InlineButton
          size={ControlSizesMD}
          centerIcon={IconCross}
          className={classes.iconButton}
          isWithoutBorder
          isWithoutBackground
          onClick={onCloseApp}
          aria-label="Close app"
          title="Close"
          dataTestId="header-right-close"
        />
      )}
    </div>
  );
};

export default HeaderRight;
